import { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Stack,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";

export default function TemplateSelectionSection({
  templates,
  loadingTemplates,
  selectedTemplate,
  variableMapping,
  csvHeaders,
  errors,
  onSelectTemplate,
  onMapVariable,
}) {
  const theme = useTheme();
  const [search, setSearch] = useState("");

  const filtered = templates.filter(
    (t) =>
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.category?.toLowerCase().includes(search.toLowerCase())
  );

  const mappingOptions = ["phone", "name", ...(csvHeaders || []).filter(h => h !== "phone" && h !== "name")];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2.5, md: 3 },
        mb: 3,
        borderRadius: 3,
        borderColor: errors.template ? theme.palette.error.main : "divider",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
        <DescriptionIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary" }}>
          Template Selection
        </Typography>
      </Stack>

      {loadingTemplates ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569" }}>
                WhatsApp Template
              </Typography>
              <Chip
                label="Required"
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 600,
                  bgcolor: theme.palette.primary.main,
                  color: "#fff",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              />
            </Stack>
            <FormControl fullWidth size="small">
              <Select
                value={selectedTemplate?.id || ""}
                onChange={(e) => {
                  const tpl = templates.find((t) => t.id === e.target.value);
                  onSelectTemplate(tpl || null);
                }}
                displayEmpty
                renderValue={(val) =>
                  val
                    ? templates.find((t) => t.id === val)?.name || val
                    : <span style={{ color: "#94a3b8" }}>Select a template</span>
                }
                sx={selectSx}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 2.5,
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                      mt: 0.5,
                      maxHeight: 360,
                    },
                  },
                }}
              >
                <Box sx={{ px: 1.5, py: 1, position: "sticky", top: 0, bgcolor: "#fff", zIndex: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search templates..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  />
                </Box>
                {filtered.map((t) => (
                  <MenuItem
                    key={t.id}
                    value={t.id}
                    sx={{
                      py: 1.25,
                      px: 2,
                      mx: 0.5,
                      borderRadius: 1.5,
                      mb: 0.25,
                      bgcolor: selectedTemplate?.id === t.id ? `${theme.palette.primary.main}0A` : "transparent",
                      "&:hover": { bgcolor: `${theme.palette.primary.main}08` },
                      "&.Mui-selected": {
                        bgcolor: `${theme.palette.primary.main}0A`,
                        "&:hover": { bgcolor: `${theme.palette.primary.main}12` },
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: selectedTemplate?.id === t.id ? 600 : 400,
                          color: selectedTemplate?.id === t.id ? theme.palette.primary.main : "text.primary",
                        }}
                      >
                        {t.name}
                      </Typography>
                      <Stack direction="row" spacing={0.75}>
                        <Chip
                          label={t.category}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 600,
                            bgcolor: "#f1f5f9",
                            color: "#64748b",
                          }}
                        />
                        <Chip
                          label={t.language}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 600,
                            borderColor: "#e2e8f0",
                            color: "#64748b",
                          }}
                        />
                      </Stack>
                    </Stack>
                  </MenuItem>
                ))}
                {filtered.length === 0 && (
                  <MenuItem disabled>
                    <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>
                      No approved templates found
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            {errors.template && (
              <Typography sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>
                {errors.template}
              </Typography>
            )}
          </Box>

          {selectedTemplate?.body?.variables?.length > 0 && (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475569" }}>
                  Variable Mapping
                </Typography>
                <Chip
                  label="Required"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontWeight: 600,
                    bgcolor: theme.palette.primary.main,
                    color: "#fff",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                />
              </Stack>
              {errors.variableMapping && (
                <Alert severity="error" sx={{ mb: 1.5, fontSize: 12, borderRadius: 2 }}>
                  {errors.variableMapping}
                </Alert>
              )}
              <Stack spacing={1.5}>
                {selectedTemplate.body.variables.map((v) => (
                  <Stack
                    key={v.index}
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Chip
                      label={`{{${v.index}}}`}
                      size="small"
                      sx={{
                        fontFamily: "monospace",
                        bgcolor: theme.palette.primary.main,
                        color: "#fff",
                        fontWeight: 600,
                        height: 24,
                        fontSize: 11,
                      }}
                    />
                    <Typography sx={{ fontSize: 13, color: "#94a3b8", minWidth: 20 }}>
                      →
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value={variableMapping[v.index] || ""}
                        onChange={(e) => onMapVariable(v.index, e.target.value)}
                        displayEmpty
                        renderValue={(val) =>
                          val || <span style={{ color: "#94a3b8" }}>Map to field</span>
                        }
                        sx={selectSx}
                      >
                        {mappingOptions.map((opt) => (
                          <MenuItem key={opt} value={opt} sx={{ fontSize: 13 }}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      )}
    </Paper>
  );
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#fff",
    borderRadius: 2,
    fontSize: 13,
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
};

const selectSx = {
  bgcolor: "#f8fafc",
  borderRadius: 2,
  fontSize: 13,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
};
